import { Injectable } from '@angular/core';

import { map, Observable, shareReplay, switchMap } from 'rxjs';

import { ALL_FUNCTIONAL_GROUPS_QUERY, ALL_GROUPS_QUERY, NEXUS_ALL_USERS_QUERY, NEXUS_USER_QUERY } from 'src/graphql/queries/abstraction-layer-queries';
import { FunctionalGroup } from 'src/models/functional-group';
import { GraphqlNexusUser } from 'src/models/graphql/graphqlNexusUser';
import { Group } from 'src/models/group';
import { NexusUser } from 'src/models/nexusUser';
import { ApolloService } from 'src/services/apollo.service';
import { BaseAPIService } from 'src/services/base-api.service';

/**
 * Contains queries related to Nexus user information and groups.
 */
@Injectable({
	providedIn: 'root',
})
export class NexusService extends BaseAPIService {
	/**
	 * Shared observable of all functional groups.
	 */
	public allFunctionalGroups$ = this.apolloService.abstractionLayerClient$.pipe(
		switchMap(client =>
			client.query<{ nexusAllFunctionalGroups: FunctionalGroup[] }>({
				query: ALL_FUNCTIONAL_GROUPS_QUERY,
			}),
		),
		map(result => result.data.nexusAllFunctionalGroups ?? []),
		shareReplay(1),
	);

	/**
	 * Shared observable of all groups.
	 */
	public allGroups$ = this.apolloService.abstractionLayerClient$.pipe(
		switchMap(client =>
			client.query<{ nexusAllGroups: Group[] }>({
				query: ALL_GROUPS_QUERY,
			}),
		),
		map(result => result.data.nexusAllGroups ?? []),
		shareReplay(1),
	);

	/**
	 * Shared observable of all nexus users.
	 */
	public allNexusUsers$ = this.apolloService.abstractionLayerClient$.pipe(
		switchMap(client =>
			client.query<{ nexusAllUsers: NexusUser[] }>({
				query: NEXUS_ALL_USERS_QUERY,
			}),
		),
		map(result => result.data.nexusAllUsers ?? []),
		shareReplay(1),
	);

	/**
	 * Constructor.
	 * @param apolloService To request data from graphql server.
	 */
	constructor(private apolloService: ApolloService) {
		super();
	}

	/**
	 * Gets a single nexus user by username.
	 * @param username Username of user to get.
	 * @returns Observable of user.
	 */
	public getNexusUser$(username: string): Observable<NexusUser | null> {
		return this.apolloService.abstractionLayerClient$.pipe(
			switchMap(client =>
				client.query<{ nexusUser: GraphqlNexusUser }>({
					query: NEXUS_USER_QUERY,
					variables: { username: username },
				}),
			),
			map(result => {
				const data = result.data.nexusUser;

				if (data && data.success) {
					return JSON.parse(data.returnValue) as NexusUser;
				}
				return null;
			}),
		);
	}
}
